import * as React from "react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useStores } from "hooks";
import { useStyles } from './GooglePayButton.styles';
import {
  FastPayData,
  IWindowCustom,
  GooglePaymentRequest,
  Confirmation3DS2,
  GoogleErrorStatuses,
  GooglePayError
} from "types";
import { formatAmount } from "utils";
import { TEXT } from "texts";
import { paths } from "routing";

declare const window: IWindowCustom;

interface IGooglePayButton {
  onSend3DSData(data: Confirmation3DS2): void
}

const GooglePayButton: React.FC<IGooglePayButton> = ({ onSend3DSData }) => {
  const { formStore, appStore, errorStore } = useStores();
  const { i18n } = useTranslation();
  const history = useHistory();
  const googleBtnRef = React.useRef<HTMLDivElement>(null);
  const [isReadyToPay, setIsReadyToPay] = useState(false);
  const [prevLng, setPrevLng] = useState<string | null>(null);

  const classes = useStyles();

  const onGooglePayLoad = () => {
    const baseRequest: Partial<GooglePaymentRequest> = {
      apiVersion: 2,
      apiVersionMinor: 0
    };
    const tokenizationSpecification = {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        'gateway': "sensebank",
        'gatewayMerchantId': formStore.googlePaymentData?.googlePayGatewayMerchantId as string,
      }
    };
    const allowedCardNetworks = ["MASTERCARD", "VISA"];
    const allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];
    const baseCardPaymentMethod = {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: allowedCardAuthMethods,
        allowedCardNetworks: allowedCardNetworks
      }
    };
    const cardPaymentMethod = Object.assign(
      { tokenizationSpecification: tokenizationSpecification },
      baseCardPaymentMethod
    );

    const paymentsClient = new window.google.payments.api.PaymentsClient({
      environment: process.env.NODE_ENV === "development" ? "TEST" : "PRODUCTION"
    });
    const isReadyToPayRequest = Object.assign({}, baseRequest);

    isReadyToPayRequest.allowedPaymentMethods = [baseCardPaymentMethod];

    paymentsClient.isReadyToPay(isReadyToPayRequest)
      .then((response: any) => {
        if (response.result) {
          setIsReadyToPay(true);

          // add a Google Pay payment button
          const button = paymentsClient.createButton({
            buttonType: 'plain',
            buttonLocale: 'uk',
            buttonSizeMode: 'fill',
            onClick: loadPayment
          });
          googleBtnRef?.current?.appendChild(button);
          // use current language
          changeButtonLanguage(i18n.language);
        }
      })
      .catch((err: Error) => {
        console.log(err.message);
      })

    const loadPayment = async () => {
      if (!formStore.amount.isValid()) { return }

      const paymentDataRequest = Object.assign({}, baseRequest);

      paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod];
      paymentDataRequest.transactionInfo = {
        totalPrice: formatAmount({amount: formStore.amount.value, rounding: false, trim: true}) as string,
        totalPriceStatus: 'FINAL',
        countryCode: "UA",
        currencyCode: 'UAH',
      };
      paymentDataRequest.merchantInfo = {
        merchantName: 'SenseBank',
        merchantId: formStore.googlePaymentData?.googlePayMerchantId as string
      };

      try {
        if (formStore.isPaymentCardVisible) {
          formStore.togglePaymentCardVisible();
          formStore.clearCardValues();
        }

        const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
        const paymentResponseParams: FastPayData = {
          paymentMethod: {
            displayName: paymentData.paymentMethodData.description,
            network: paymentData.paymentMethodData.info.cardNetwork,
            type: 'unknown',
          },
          sourceGooglePayPaymentData: btoa(paymentData.paymentMethodData.tokenizationData.token)
        }

        formStore.setFastPayData(paymentResponseParams);

        const response = await formStore.submit();

        if (response.orderStatus) {
          const isResponseSuccess = response.orderStatus === 'CONFIRMED' || response.orderStatus === 'PROCESSED';
          history.push(isResponseSuccess ? paths.success : paths.error);
        }

        if (response.confirmationType === '3DS2' && response.confirmation3DS2) {
          appStore.setOrderId(response.orderId);
          onSend3DSData(response.confirmation3DS2);
        }
      } catch (error) {
        const closeErrorMessageDesktop = 'User closed the Payment Request UI.'
        const closeErrorStatusMobile: GoogleErrorStatuses = "CANCELED";

        if ((error as Error).message !== closeErrorMessageDesktop && (error as GooglePayError).statusCode !== closeErrorStatusMobile) {
          errorStore.setError({ title: TEXT.error, description: (error as Error).message, callback: () => errorStore.setError({}) });
        }
      }
    }
  }

  const changeButtonLanguage = (lang: string) => {
    const buttonIn = googleBtnRef?.current?.querySelector('button');
    let url = buttonIn && window.getComputedStyle(buttonIn).backgroundImage;
    let urlBracketsIndex = -2 // last closing brackets ")

    if (!prevLng) {
      // button language init by google
      setPrevLng(navigator.language.split('-')[0]) // replace 'en-US' && 'en' => 'en'
    }

    if (lang === 'ua') { lang = 'uk' }

    if (url && !~url.indexOf('"')) {
      urlBracketsIndex = -1 // For Edge (dont have " in url)
    }

    if (url && url.slice(url.indexOf(prevLng as string) + 2, urlBracketsIndex) === '.svg') { // check url is valid
      url = url.replace(prevLng as string, lang);
      buttonIn && (buttonIn.style.backgroundImage = url);
    } else {
      console.warn('Invalid Google Pay button image url')
    }
    // save last language
    setPrevLng(lang);
  }

  useEffect(() => {
    // Load Google Pay API
    const head = document.head || document.getElementsByTagName('head')[0]
    const script = document.createElement('script')
    script.charset = "UTF-8"
    script.src = "https://pay.google.com/gp/p/js/pay.js" // TODO: move url to ENV
    script.async = true
    script.onload = onGooglePayLoad
    head.appendChild(script)
    // Language change bind
    i18n.on('languageChanged', (lang) => {
      changeButtonLanguage(lang)
    })

    return () => {
      document.querySelectorAll('iframe').forEach((iframe)=> {
        if (iframe.src.includes('https://pay.google.com')) {
          iframe?.parentNode?.removeChild(iframe)
        }
      });
      script?.parentNode?.removeChild(script);
    }
  },[]);

  if (!isReadyToPay) return null;

  return (
    <div ref={googleBtnRef} className={classes.payBtnWrapper}/>
  );
};

export default observer(GooglePayButton)
